import { useEffect, useState } from "react";
import { AgentCursor } from "@/components/cursors/agent-cursor";
import { FLUID_POINTER_DAMPING_SECONDS } from "@/components/cursors/cursor-animation-geometry";
import { useAppLanguage } from "@/i18n";
import { useCanvasStore } from "@/stores/canvas-store";
import { useShortcutStore } from "@/stores/shortcut-store";
import { COMPACT_CURSOR_POINTER_OFFSET } from "./compact-agent-cursor-state";
import { CompactCursorPill } from "./compact-cursor-pill";
import { greetingText } from "./idle-greeting-copy";
import { greetingHoldMs, useIdleGreeting } from "./idle-greeting-store";
import { useActiveDisplay } from "./use-active-display";
import { useDampedFollow } from "./use-damped-follow";
import { getWindowDisplayId } from "./use-window-role";
import { useWindowOrigin } from "./use-window-origin";

/** A greeting the owner could not clear (reload, role change) ends here. */
const STALE_GRACE_MS = 2_000;

type HeldGreeting = { id: number; text: string };

/** The greeting, spoken by the agent cursor beside the user's pointer the
 *  way a reply is: same pill, same reveal, then it retracts on its own. */
export function AgentCursorIdleGreeting({ visible }: { visible: boolean }) {
  const greeting = useIdleGreeting((state) => state.greeting);
  // Worded from the saved shortcuts and the app language: re-word on change.
  useShortcutStore((state) => state.overrides);
  useShortcutStore((state) => state.disabled);
  useAppLanguage();
  const agentCursorUp = useCanvasStore((state) => state.agentCursors.size > 0);
  const activeDisplayId = useActiveDisplay((state) => state.activeDisplayId);
  const winOrigin = useWindowOrigin();
  // Retracted, acted on or stale here: never replayed on this display.
  const [doneId, setDoneId] = useState<number | null>(null);
  const greetingId = greeting?.id ?? null;
  const staleAt = greeting
    ? greeting.id + greetingHoldMs(greeting.message) + STALE_GRACE_MS
    : null;
  useEffect(() => {
    if (greetingId === null || staleAt === null) return;
    const timer = window.setTimeout(
      () => setDoneId(greetingId),
      Math.max(0, staleAt - Date.now()),
    );
    return () => window.clearTimeout(timer);
  }, [greetingId, staleAt]);
  // Only beside a pointer whose place is known: never at a default spot.
  const underPointer =
    activeDisplayId !== null && activeDisplayId === getWindowDisplayId();
  const text = greeting ? greetingText(greeting.message) : null;
  const live =
    visible &&
    underPointer &&
    !agentCursorUp &&
    winOrigin.ready &&
    greeting &&
    greeting.id !== doneId &&
    text
      ? { id: greeting.id, text }
      : null;
  // Hold the last text through the pill's retract.
  const [held, setHeld] = useState<HeldGreeting | null>(live);
  if (live && (live.id !== held?.id || live.text !== held.text)) setHeld(live);
  if (!held || !winOrigin.ready) return null;
  return (
    <GreetingCursor
      greeting={held}
      visible={!!live}
      onExited={() => {
        setDoneId(held.id);
        setHeld(null);
      }}
    />
  );
}

function GreetingCursor({
  greeting,
  visible,
  onExited,
}: {
  greeting: HeldGreeting;
  visible: boolean;
  onExited: () => void;
}) {
  const cursorX = useActiveDisplay((state) => state.cursorX);
  const cursorY = useActiveDisplay((state) => state.cursorY);
  const follow = useDampedFollow(FLUID_POINTER_DAMPING_SECONDS);
  follow.setTarget({
    x: cursorX + COMPACT_CURSOR_POINTER_OFFSET,
    y: cursorY + COMPACT_CURSOR_POINTER_OFFSET,
  });
  return (
    <div
      ref={follow.elementRef}
      data-testid="desktop-agent-cursor-greeting"
      style={{
        position: "fixed",
        left: 0,
        top: 0,
        pointerEvents: "none",
        zIndex: 9999,
      }}
    >
      <AgentCursor
        agentName="Denker"
        agentColor="var(--color-accent)"
        pointing={false}
        className="origin-top-left"
        statusPill={
          <CompactCursorPill
            key={greeting.id}
            content={{ kind: "reply", text: greeting.text, terminal: false }}
            visible={visible}
            onExited={onExited}
          />
        }
      />
    </div>
  );
}
